import React from 'react';
import moment from 'moment';
import styles from './BlockAttendance.module.css';

export interface BlockAttendanceDay {
  date: string; // "YYYY-MM-DD"
  presentCount: number | null;
  absentCount: number | null;
  otherCount: number | null;
  attendancePercentage: number | null;
}

export interface DroppedStudentReport {
  userId: number;
  name: string | null;
  identifier: string | null;
  phone: string | null;
  courseId: number;
  courseName: string | null;
  courseSKU: string | null;
  lastSessionDate: string | null; // ISO 8601, null if no attendance rows
}

interface BlockAttendanceGraphProps {
  days: BlockAttendanceDay[];
  droppedStudents: DroppedStudentReport[];
}

// Fixed drawing space; the SVG scales to 100% width via viewBox.
const WIDTH = 1200;
const HEIGHT = 320;
const PAD_LEFT = 48;
const PAD_RIGHT = 16;
const PAD_TOP = 16;
const PAD_BOTTOM = 40;
const PLOT_W = WIDTH - PAD_LEFT - PAD_RIGHT;
const PLOT_H = HEIGHT - PAD_TOP - PAD_BOTTOM;

const NAVY = '#13325e';
const ORANGE = '#f08b00';
const RED = '#e2574c';
const GRID_GRAY = '#e0e0e0';
const LABEL_GRAY = '#8a93a3';

const MAX_X_LABELS = 14;

const BlockAttendanceGraph: React.FC<BlockAttendanceGraphProps> = ({ days, droppedStudents }) => {
  if (days.length === 0) return null;

  const t0 = moment(days[0].date, 'YYYY-MM-DD').valueOf();
  const t1 = moment(days[days.length - 1].date, 'YYYY-MM-DD').valueOf();
  const span = Math.max(t1 - t0, 1);

  const x = (t: number) => PAD_LEFT + ((t - t0) / span) * PLOT_W;
  const y = (pct: number) => PAD_TOP + (1 - pct / 100) * PLOT_H;

  // Weekly vertical gridlines across the span (ISO week boundaries).
  const weekLines: number[] = [];
  for (
    let w = moment(t0).startOf('isoWeek').add(1, 'week');
    w.valueOf() < t1;
    w.add(1, 'week')
  ) {
    if (w.valueOf() > t0) weekLines.push(w.valueOf());
  }

  // Red drop lines: group dropped students by org-local day, skip null dates.
  const dropsByDay = new Map<string, DroppedStudentReport[]>();
  droppedStudents.forEach((s) => {
    if (!s.lastSessionDate) return;
    const day = moment(s.lastSessionDate).format('YYYY-MM-DD');
    const list = dropsByDay.get(day) ?? [];
    list.push(s);
    dropsByDay.set(day, list);
  });

  // Line + dots for days with a recorded percentage.
  const plotted = days.filter((d) => d.attendancePercentage != null);
  const linePoints = plotted
    .map((d) => `${x(moment(d.date, 'YYYY-MM-DD').valueOf()).toFixed(1)},${y(d.attendancePercentage as number).toFixed(1)}`)
    .join(' ');

  // Thin the x-axis labels so they don't overlap over a ~12-week block.
  const labelEvery = Math.max(1, Math.ceil(days.length / MAX_X_LABELS));

  return (
    <div className={styles.graphWrapper}>
      <svg
        className={styles.graphSvg}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role='img'
        aria-label='Daily attendance percentage for the selected block'
      >
        {/* Horizontal reference lines + y labels (0–100%) */}
        {[0, 25, 50, 75, 100].map((pct) => (
          <g key={pct}>
            <line
              x1={PAD_LEFT}
              y1={y(pct)}
              x2={WIDTH - PAD_RIGHT}
              y2={y(pct)}
              stroke={pct === 0 ? NAVY : '#eef1f6'}
              strokeWidth={pct === 0 ? 2 : 1}
            />
            <text
              x={PAD_LEFT - 8}
              y={y(pct) + 4}
              textAnchor='end'
              fontSize={11}
              fontWeight={600}
              fill={LABEL_GRAY}
            >
              {pct}%
            </text>
          </g>
        ))}

        {/* Y axis */}
        <line x1={PAD_LEFT} y1={PAD_TOP} x2={PAD_LEFT} y2={PAD_TOP + PLOT_H} stroke={NAVY} strokeWidth={2} />

        {/* Week-boundary vertical gridlines */}
        {weekLines.map((t) => (
          <line
            key={t}
            x1={x(t)}
            y1={PAD_TOP}
            x2={x(t)}
            y2={PAD_TOP + PLOT_H}
            stroke={GRID_GRAY}
            strokeWidth={1}
          />
        ))}

        {/* Red vertical lines at dropped students' last session dates */}
        {Array.from(dropsByDay.entries()).map(([day, students]) => {
          const t = moment(day, 'YYYY-MM-DD').valueOf();
          if (t < t0 || t > t1) return null;
          const label = students
            .map((s) => `${s.name ?? 'Unknown'} (${s.courseName ?? '-'})`)
            .join('\n');
          return (
            <g key={day} className={styles.dropLine}>
              <title>{`Dropped — last session ${moment(day, 'YYYY-MM-DD').format('D MMM YYYY')}:\n${label}`}</title>
              {/* Wide invisible hit area so the tooltip is easy to reach */}
              <line x1={x(t)} y1={PAD_TOP} x2={x(t)} y2={PAD_TOP + PLOT_H} stroke='transparent' strokeWidth={10} />
              <line x1={x(t)} y1={PAD_TOP} x2={x(t)} y2={PAD_TOP + PLOT_H} stroke={RED} strokeWidth={2} />
            </g>
          );
        })}

        {/* Attendance % line */}
        {plotted.length > 1 && (
          <polyline points={linePoints} fill='none' stroke={NAVY} strokeWidth={2} />
        )}

        {/* Dots with per-day tooltips */}
        {plotted.map((d) => {
          const t = moment(d.date, 'YYYY-MM-DD').valueOf();
          const pct = d.attendancePercentage as number;
          return (
            <circle key={d.date} cx={x(t)} cy={y(pct)} r={3.5} fill={ORANGE} stroke='#fff' strokeWidth={1}>
              <title>{`${moment(d.date, 'YYYY-MM-DD').format('D MMM YYYY')} — ${Math.round(pct)}% (${d.presentCount ?? 0} present, ${d.absentCount ?? 0} absent, ${d.otherCount ?? 0} other)`}</title>
            </circle>
          );
        })}

        {/* X axis date labels, thinned */}
        {days.map((d, i) => {
          if (i % labelEvery !== 0 && i !== days.length - 1) return null;
          const t = moment(d.date, 'YYYY-MM-DD').valueOf();
          return (
            <text
              key={d.date}
              x={x(t)}
              y={PAD_TOP + PLOT_H + 18}
              textAnchor='middle'
              fontSize={11}
              fontWeight={600}
              fill={LABEL_GRAY}
            >
              {moment(d.date, 'YYYY-MM-DD').format('D MMM')}
            </text>
          );
        })}
      </svg>

      <div className={styles.legend}>
        <span className={styles.legendItem}><span className={styles.swatchLine}></span> Attendance %</span>
        <span className={styles.legendItem}><span className={styles.swatchDrop}></span> Student dropped (last session)</span>
      </div>
    </div>
  );
};

export { BlockAttendanceGraph };
